import { useEffect } from "react";
import {
  SandpackProvider,
  SandpackLayout,
  SandpackCodeEditor,
  SandpackPreview,
  SandpackFileExplorer,
  useSandpack,
  type SandpackFiles,
} from "@codesandbox/sandpack-react";

const DEFAULT_FILES: SandpackFiles = {
  "/index.html": `<!doctype html>
<html>
  <head><link rel="stylesheet" href="styles.css" /></head>
  <body>
    <h1>Hello, web!</h1>
    <script src="app.js"></script>
  </body>
</html>
`,
  "/styles.css": `body { font-family: system-ui, sans-serif; padding: 24px; }
h1 { color: #4f46e5; }
`,
  "/app.js": `console.log('Hello from JS');
`,
};

/**
 * Web sandbox starter contract.
 *
 * The canonical format authored by instructors is a JSON object with optional
 * `html`, `css`, and `js` keys, which map to `/index.html`, `/styles.css`, and
 * `/app.js` inside Sandpack. We also accept (for backwards compatibility):
 *   - a JSON map of explicit Sandpack paths (e.g. `{"/index.html": "..."}`),
 *   - or a single raw HTML string (used as `/index.html`).
 *
 * `serializeWebFiles` writes the canonical `{ html, css, js }` form back
 * whenever the user's edits stay within the three standard files, so saves
 * round-trip cleanly with the spec.
 */
const HTML_FILE = "/index.html";
const CSS_FILE = "/styles.css";
const JS_FILE = "/app.js";

function fromCanonical(parsed: Record<string, unknown>): SandpackFiles | null {
  const hasCanonicalKey =
    typeof parsed.html === "string" ||
    typeof parsed.css === "string" ||
    typeof parsed.js === "string";
  if (!hasCanonicalKey) return null;
  const out: SandpackFiles = {
    [HTML_FILE]:
      typeof parsed.html === "string" ? parsed.html : DEFAULT_FILES[HTML_FILE]!,
    [CSS_FILE]:
      typeof parsed.css === "string" ? parsed.css : DEFAULT_FILES[CSS_FILE]!,
    [JS_FILE]:
      typeof parsed.js === "string" ? parsed.js : DEFAULT_FILES[JS_FILE]!,
  };
  return out;
}

export function parseWebStarter(starter: string): SandpackFiles {
  if (!starter.trim()) return DEFAULT_FILES;
  try {
    const parsed = JSON.parse(starter);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const canonical = fromCanonical(parsed as Record<string, unknown>);
      if (canonical) return canonical;
      const out: SandpackFiles = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "string") out[k] = v;
      }
      // Make sure the static template always has an entry HTML file.
      if (!out[HTML_FILE] && typeof out["index.html"] === "string") {
        out[HTML_FILE] = out["index.html"] as string;
      }
      if (!out[HTML_FILE]) out[HTML_FILE] = DEFAULT_FILES[HTML_FILE]!;
      if (Object.keys(out).length > 0) return out;
    }
  } catch {
    return { [HTML_FILE]: starter };
  }
  return DEFAULT_FILES;
}

function fileText(v: SandpackFiles[string]): string {
  return typeof v === "string" ? v : v.code;
}

export function serializeWebFiles(files: SandpackFiles): string {
  const keys = Object.keys(files);
  const standardOnly =
    keys.length > 0 &&
    keys.every((k) => k === HTML_FILE || k === CSS_FILE || k === JS_FILE);
  if (standardOnly) {
    const out: Record<string, string> = {};
    if (files[HTML_FILE] !== undefined) out.html = fileText(files[HTML_FILE]!);
    if (files[CSS_FILE] !== undefined) out.css = fileText(files[CSS_FILE]!);
    if (files[JS_FILE] !== undefined) out.js = fileText(files[JS_FILE]!);
    return JSON.stringify(out);
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(files)) {
    out[k] = fileText(v);
  }
  return JSON.stringify(out);
}

function FilesBridge({
  onChange,
}: {
  onChange: (files: SandpackFiles) => void;
}) {
  const { sandpack } = useSandpack();
  useEffect(() => {
    onChange(sandpack.files);
  }, [sandpack.files, onChange]);
  return null;
}

export function WebSandbox(props: {
  files: SandpackFiles;
  onFilesChange?: (files: SandpackFiles) => void;
  readOnly?: boolean;
  remountKey?: string | number;
}) {
  const { files, onFilesChange, readOnly, remountKey } = props;
  return (
    <SandpackProvider
      key={remountKey}
      template="static"
      theme="dark"
      files={files}
      options={{ recompileMode: "delayed", recompileDelay: 400 }}
    >
      <SandpackLayout>
        <SandpackFileExplorer />
        <SandpackCodeEditor
          showLineNumbers
          showTabs
          readOnly={readOnly}
          style={{ height: 380 }}
        />
        <SandpackPreview style={{ height: 380 }} />
      </SandpackLayout>
      {onFilesChange && <FilesBridge onChange={onFilesChange} />}
    </SandpackProvider>
  );
}

export { DEFAULT_FILES };
